import { Box, IconButton } from '@mui/material';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';

import { FeedbackStarIcon } from './FeedbackStarIcon';
import {
  FEEDBACK_STAR_COUNT,
  FEEDBACK_STAR_INDICES,
} from './feedbackStarRating.constants';
import { getFeedbackStarIconDisplayMode } from './feedbackStarRating.utils';

const TRANSLATION_KEYS = {
  starsLabel: 'feedback:rating.starsLabel',
  starsLabelSelected: 'feedback:rating.starsLabelSelected',
} as const;

export type FeedbackStarRatingProps = {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  disabled?: boolean;
  /** Id of the visible label for the rating group. */
  'aria-labelledby'?: string;
  rowClassName: string;
};

/**
 * 1–5 star control; `value` undefined means no selection. Tapping the current
 * rating again clears the selection (optional star rating in the API).
 * Hover previews fill; at 4+ stars the fill color switches to green (design).
 * One path shape, colors from state — no per-state SVG files.
 */
function FeedbackStarRatingInner({
  value,
  onChange,
  disabled,
  'aria-labelledby': ariaLabelledBy,
  rowClassName,
}: FeedbackStarRatingProps) {
  useTranslation(['feedback']);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `value` is a change trigger to clear the hover preview, not read in the effect body
  useEffect(() => {
    setHoverIndex(null);
  }, [value]);

  const handleClick = useCallback(
    (n: (typeof FEEDBACK_STAR_INDICES)[number]) => {
      onChange(value === n ? undefined : n);
    },
    [onChange, value]
  );

  const displayLevel = hoverIndex != null ? hoverIndex : (value ?? 0);

  const clearHover = useCallback(() => {
    setHoverIndex(null);
  }, []);

  const handleStarEnter = useCallback(
    (n: (typeof FEEDBACK_STAR_INDICES)[number]) => {
      if (!disabled) {
        setHoverIndex(n);
      }
    },
    [disabled]
  );

  const starButtonSx = useMemo(
    () => ({
      padding: 0,
      width: 32,
      height: 32,
      // Outline stars use stroke="currentColor" in FeedbackStarIcon
      color: 'text.secondary',
    }),
    []
  );

  return (
    <Box
      className={rowClassName}
      display="flex"
      gap="4px"
      role="radiogroup"
      aria-labelledby={ariaLabelledBy}
      onPointerLeave={clearHover}
    >
      {FEEDBACK_STAR_INDICES.map((n) => {
        const starMode = getFeedbackStarIconDisplayMode(n, displayLevel);
        const isSelected = value === n;
        return (
          <IconButton
            key={n}
            type="button"
            role="radio"
            aria-checked={isSelected}
            data-testid={`feedback-star-${n}`}
            data-star-fill={starMode}
            disabled={disabled}
            onClick={() => handleClick(n)}
            onPointerEnter={() => {
              handleStarEnter(n);
            }}
            aria-label={translateText(
              isSelected
                ? TRANSLATION_KEYS.starsLabelSelected
                : TRANSLATION_KEYS.starsLabel,
              { rating: n, total: FEEDBACK_STAR_COUNT }
            )}
            sx={starButtonSx}
          >
            <FeedbackStarIcon displayMode={starMode} />
          </IconButton>
        );
      })}
    </Box>
  );
}

export const FeedbackStarRating = memo(FeedbackStarRatingInner);
